import axios from 'axios';
import DiscountCodeNotFoundError from '@root/lib/errors/DiscountCodeNotFoundError';
import GraphqlError from '@root/lib/errors/GraphqlError';
import UnidentifiedCustomerError from '@root/lib/errors/UnidentifiedCustomerError';

const client = axios.create();
client.interceptors.request.use((config) => {
  config.url = `https://${Shopify.shop}/api/2023-10/graphql.json`;
  config.headers = {
    ...config.headers,
    'X-Shopify-Storefront-Access-Token': window.ENV.storefrontApiKey,
  };

  return config;
});

/**
 * @param {Array<string|string[]>} arr
 */
const toQuery = (arr) => {
  if (arr === undefined) return undefined;
  let s = '';

  for (const field of arr) {
    s += Array.isArray(field)
      ? ` {\n${toQuery(field)}\n}`
      : `\n${field}`;
  }

  return s.trim();
}

/**
 * Checks a response for errors and throws appropriate errors if errors were
 * found in response
 *
 * @param {{
 *   errors?: Array;
 * }} resp
 * @param {{
 *   discountCode?: string;
 *   variables?: Object;
 *   query: string;
 * }} kwargs
 */
const checkResponse = (resp, kwargs) => {
  if (resp.errors.length === 0) return;
  const [ firstError ] = resp.errors;

  switch (firstError.code) {
    case 'DISCOUNT_NOT_FOUND': {
      let { discountCode, query, variables } = kwargs;
      throw new DiscountCodeNotFoundError('Discount code not found', query, discountCode, {variables});
    } case 'UNIDENTIFIED_CUSTOMER': {
      let { variables, query } = kwargs;
      throw new UnidentifiedCustomerError(firstError.message, query, {variables});
    } default: {
      const { query, variables } = kwargs;
      throw new GraphqlError(firstError.message, query, {variables});
    }
  }
}

/**
 * Gets variants by their ID and returns them in a map with the index being variant ids
 * 
 * @param {string[]|number[]} variantIds
 * @param {Array<string|string[]>} fragment
 *
 * @returns {Promise<Map<string, Object>>}
 */
const getVariantsById = async (variantIds, fragment) => {
  const query = `
    {
      ${variantIds.map(variantId =>
        `v${variantId}: node(id: "${btoa(`gid://shopify/ProductVariant/${variantId}`)}") {
          ...variant
        }`
      )}
    }

    fragment variant on ProductVariant {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query});
  return new Map(Object.entries(resp.data.data));
}

/**
 * Gets products by their IDs and returns them in a map indexed by their ids. If a product
 * could not be fetched, it's value in the map will be null
 * 
 * @param {string[]|number[]} productIds
 * @param {Array<string|string[]>} fragment
 *
 * @returns {Promise<Map<number, Object>>}
 */
 const getProductsById = async (productIds, fragment) => {
  const query = `
    {
      ${productIds.map(productId =>
        `v${productId}: node(id: "${btoa(`gid://shopify/ProductVariant/${productId}`)}") {
          ...product
        }`
      )}
    }

    fragment variant on Product {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query});
  const productMap = new Map();

  for (const [ key, product ] of resp.data.data) {
    const id = parseInt(key.slice(1));
    productMap.set(id, product);
  }

  return productMap;
}

/**
 * Sets a discount code on a checkout
 *
 * @param {string} checkoutId
 * @param {string} discountCode
 * @param {Array<string|string[]>} fragment
 */
const setCheckoutDiscountCode = async (checkoutId, discountCode, fragment) => {
  const variables = {discountCode, checkoutId};
  const query = `
    mutation ($discountCode: String!, $checkoutId: ID!) {
      checkoutDiscountCodeApplyV2 (discountCode: $discountCode, checkoutId: $checkoutId) {
        checkout {
          ...frag
        }
        errors: checkoutUserErrors {
          code
          field
          message
        }
      }
    }

    fragment frag on Checkout {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query, variables});
  const data = resp.data.data?.checkoutDiscountCodeApplyV2 ?? resp.data;
  checkResponse(data, {query, variables, discountCode});

  return data.checkout;
}

/**
 * Removes a discount code from a checkout
 *
 * @param {string} checkoutId
 * @param {Array<string|string[]>} fragment
 */
const removeCheckoutDiscountCode = async (checkoutId, fragment) => {
  const variables = {checkoutId};
  const query = `
    mutation ($checkoutId: ID!) {
      checkoutDiscountCodeRemove (checkoutId: $checkoutId) {
        checkout {
          ...frag
        }
        errors: checkoutUserErrors {
          code
          field
          message
        }
      }
    }

    fragment frag on Checkout {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query, variables});
  const data = resp.data.data?.checkoutDiscountCodeRemove ?? resp.data;
  checkResponse(data, {query, variables});

  return data.checkout;
}

/**
 * Replaces a checkout's line items with the line items provided
 *
 * @param {string} checkoutId
 * @param {Object[]} lineItems
 * @param {string} fragment
 */
const replaceCheckoutLineItems = async (checkoutId, lineItems, fragment) => {
  const variables = {lineItems, checkoutId};
  const query = `
    mutation ($lineItems: [CheckoutLineItemInput!]!, $checkoutId: ID!) {
      checkoutLineItemsReplace (lineItems: $lineItems, checkoutId: $checkoutId) {
        checkout {
          ...frag
        }
      }
    }

    fragment frag on Checkout {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query, variables});
  return resp.data.data.checkoutLineItemsReplace.checkout;
}

/**
 * Adds items to the checkout
 *
 * @param {string} checkoutId
 * @param {Object[]} items
 * @param {Array<string|string[]>} fragment
 */
const addItemsToCheckout = async (checkoutId, items, fragment) => {
  const variables = {checkoutId, items};
  const query = `
    mutation ($items: [CheckoutLineItemInput!]!, $checkoutId: ID!) {
      checkoutLineItemsAdd (lineItems: $items, checkoutId: $checkoutId) {
        checkout {
          ...frag
        }
      }
    }

    fragment frag on Checkout {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query, variables});
  return resp.data.data.checkoutLineItemsAdd.checkout;
}

/**
 * Updates existing line items in checkout
 *
 * @param {string} checkoutId
 * @param {Array<{
 *   id: string;
 *   quantity?: number;
 *   customAttributes: Array;
 *   variantId?: string;
 * }>} items
 * @param {Array<string|string[]>} fragment
 */
const updateItemsInCheckout = async (checkoutId, items, fragment) => {
  const variables = {checkoutId, items};
  const query = `
    mutation ($checkoutId: ID!, $items: [CheckoutLineItemUpdateInput!]!) {
      checkoutLineItemsUpdate (checkoutId: $checkoutId, lineItems: $items) {
        checkout {
          ...frag
        }
      }
    }

    fragment frag on Checkout {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query, variables});
  return resp.data.data.checkoutLineItemsUpdate.checkout;
}

/**
 * Updates the shipping address of a checkout instance
 * 
 * @param {number} checkoutId 
 * @param {{
 *   zip?: string;
 * }} address 
 * @param {Array<string|string[]>} fragment
 */
const updateShippingAddressOnCheckout = async (checkoutId, address, fragment) => {
  const variables = {checkoutId, address};
  const query = `
    mutation ($checkoutId: ID!, $address: MailingAddressInput!) {
      checkoutShippingAddressUpdateV2 (checkoutId: $checkoutId, shippingAddress: $address) {
        checkout {
          ...frag
        }
        userErrors {
          message
        }
      }
    }

    fragment frag on Checkout {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query, variables});
  return resp.data.data.checkoutShippingAddressUpdateV2.checkout;
};

/**
 * Creates a checkout instance
 */
const initializeCheckout = async (fragment) => {
  const variables = {input: {}};
  const query = `
    mutation ($input: CheckoutCreateInput!) {
      checkoutCreate (input: $input) {
        checkout {
          ...frag
        }
      }
    }

    fragment frag on Checkout {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query, variables});
  return resp.data.data.checkoutCreate.checkout;
}

const customer = {
  /**
   * Creates a customer access token. The customer access token
   * is required to modify the customer object in any way.
   *
   * @param {string} email
   * @param {string} password
   * @param {Array<string|string[]>} fragment
   */
  createAccessToken: async (email, password, fragment) => {
    const variables = {
      input: {email, password},
    };
    const query = `
      mutation ($input: CustomerAccessTokenCreateInput!) {
        customerAccessTokenCreate(input: $input) {
          customerAccessToken {
            ...frag
          }
          errors: customerUserErrors {
            message
            code
          }
        }
      }

      fragment frag on CustomerAccessToken {
        ${toQuery(fragment)}
      }
    `;
    const resp = await client.post('', {query, variables});
    const customerAccessTokenCreate = resp.data.data.customerAccessTokenCreate;
    checkResponse(customerAccessTokenCreate, {variables, query});

    return customerAccessTokenCreate.customerAccessToken;
  },

  /**
   *
   * @param {string} token
   * @param {{
   *   acceptsMarketing?: boolean;
   *   email?: string;
   *   firstName?: string;
   *   lastName?: string;
   *   password?: string;
   *   phone?: string;
   * }} customer
   * @param {Array<string|string[]>} fragment
   */
  update: async (token, customer, fragment) => {
    const variables = {token, customer};
    const query = `
      mutation ($token: String!, $customer: CustomerUpdateInput!) {
        customerUpdate(customerAccessToken: $token, customer: $customer) {
          customer {
            ...frag
          }
        }
      }

      fragment frag on Customer {
        ${toQuery(fragment)}
      }
    `;
    const resp = await client.post('', {query, variables});
    return resp.data.data.customerUpdate.customer;
  }
}

/**
 * Gets a node by it's ID.
 *
 * @param {string} nodeId
 * @param {string} nodeType
 * @param {Array<string|string[]>} fragment
 */
const getNodeById = async (nodeId, nodeType, fragment) => {
  const query = `
    {
      node(id: "${nodeId}") {
        ...frag
      }
    }

    fragment frag on ${nodeType} {
      ${toQuery(fragment)}
    }
  `;

  const resp = await client.post('', {query});
  return resp.data.data.node;
}


const converters = {
  /**
   * Converts a GraphQL id into a numeric id
   *
   * @param {string} str
   */
  toNumericId: (str) => {
    const [ ,id ] = str.match(/^gid:\/\/shopify\/.+\/(\d+)$/);
    return parseInt(id);
  },

  /**
   * Converts a Shopify decimal into a number that represents cents
   *
   * @param {string} dec
   */
  decimalToCents: (dec) => {
    let [ dollars, cents ] = dec.split('.');
    dollars = parseInt(dollars) * 100;
    cents = parseInt((cents ?? '').padEnd(2, '0'));
    return dollars + cents;
  },
};
const decimalToCents = converters.decimalToCents;

export {
  converters,
  customer,
  getVariantsById,
  getProductsById,
  getNodeById,
  replaceCheckoutLineItems,
  addItemsToCheckout,
  initializeCheckout,
  updateItemsInCheckout,
  setCheckoutDiscountCode,
  removeCheckoutDiscountCode,
  updateShippingAddressOnCheckout,
  decimalToCents,
}
